import Component from '@glimmer/component';

import { hash } from '@ember/helper';
import noop from '@nullvoxpopuli/ember-composable-helpers/helpers/noop';
import isStateActive from 'ember-route-state/helpers/is-state-active.js';

import Button from 'rise-ui/components/c-button/button.gjs';
import CIcon from 'rise-ui/components/c-icon.gjs';
import CIdentity from 'rise-ui/components/c-identity.gjs';
import { classMix } from 'rise-ui/decorators/class-mix.js';
import and from 'rise-ui/helpers/and.js';
import bool from 'rise-ui/helpers/bool.js';
import classMod from 'rise-ui/helpers/class-mod.js';
import classModConfig from 'rise-ui/helpers/class-mod-config.js';
import concatClasses from 'rise-ui/helpers/concat-classes.js';
import isFalse from 'rise-ui/helpers/is-false.js';
import isTruthy from 'rise-ui/helpers/is-truthy.js';
import not from 'rise-ui/helpers/not.js';
import or from 'rise-ui/helpers/or.js';

/**
 * @typedef CButton
 * @type {CButton & ClassMixArgs}
 * @property {string} args.type
 */
export default class CButton extends Component {
  /** @type {ClassMix} */
  @classMix classMixed;

  get typeModifiers() {
    return [
      ...(!this.args.type ? [] : (this.args.type + '').trim().split(/\s+/))
        .reduce((p, t) => ([ ...p, true, `--${t}`]), [])
    ];
  }

  <template>
    {{~#let (or @isActive (isStateActive @linkTo)) as | isActive |~}}
    {{~#let (or @disabled (and (bool @activeDisabled) (bool isActive))) as | isDisabled |~}}
    {{~#let
      (or
        (and (not isDisabled) (or @action @onClick))
        (noop)
      )
      (classModConfig (concatClasses "c-button" this.classMixed.mixedClassNames)
        isDisabled "--disabled"
        modifiers=this.typeModifiers
      )
      (isTruthy @external)
      (isTruthy @download)
      (not (isFalse @externalIcon))
      (or @linkToExternal @linkToDownload)
      as |
        onClick classModConfig
        isExplicitlyExternal isExplicitlyDownload
        allowExternalIcon externalUrl
      |
    ~}}
      <Button
        @classModConfig={{classModConfig}}
        @linkTo={{@linkTo}}
        @externalUrl={{externalUrl}}
        @isExplicitlyExternal={{isExplicitlyExternal}}
        @target={{@target}}
        @rel={{@rel}}
        @preventDefault={{@preventDefault}}
        @defaultLink={{@defaultLink}}
        @onClick={{onClick}}
        @isActive={{isActive}}
        @disabled={{isDisabled}}
        @title={{or (if isActive @activeTitle (and @linkTo @inactiveTitle)) @title}}
        @stopPropagation={{@stopPropagation}}
        ...attributes
      >
        <span class={{classMod config=classModConfig suffix="__wrapper"}}>
          <span class={{classMod config=classModConfig suffix="__content"}}>
            {{~yield (hash default=(component CIdentity))~}}
          </span>
          {{~#if (and
            allowExternalIcon
            (or externalUrl isExplicitlyExternal isExplicitlyDownload)
          )}}
            {{#if (or
              isExplicitlyExternal
              (and @linkToExternal (not isExplicitlyDownload))
            )}}
              <CIcon
                @classMix="c-external-link__icon"
                @icon="external-link"
                @text="Opens in new window"
              />
            {{else}}
              <CIcon @icon="download" @text="Download file" />
            {{/if}}
          {{/if~}}
        </span>
      </Button>
    {{~/let~}}
    {{~/let~}}
    {{~/let~}}
  </template>
}
